import {
  effectivePalette,
  fontStack,
  RADIUS_REM,
  themeCssVariables,
  type PublicSite,
  type ThemeCustomisation,
} from '@hearth/shared';

/**
 * Applying a tenant's branding to the document. Everything here is CSP-safe: the theme and
 * the custom CSS go through constructed stylesheets (CSSOM), never a <style> element, because
 * the production policy is `style-src 'self'`. Custom CSS is scoped to `.hearth-custom`, the
 * customer shell, so it can never restyle /admin.
 */

/** The class that marks the part of the page custom CSS may reach. */
export const CUSTOM_CSS_SCOPE = '.hearth-custom';
/** What theme.js reads before first paint, so a branded default theme doesn't flash. */
export const DEFAULT_THEME_KEY = 'hearth.branding.defaultTheme';
export const THEME_SWITCH_KEY = 'hearth.branding.themeSwitch';
const DEFAULT_FAVICON = '/favicon.svg';

const declarations = (vars: Record<string, string>) =>
  Object.entries(vars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');

/**
 * The stylesheet text for a theme: the provider's light palette on `:root` (and `.light`, so
 * a forced-light login page inside a dark document works), the dark palette on `.dark`, and
 * radius and fonts on `:root`. Only the keys the provider set are emitted; index.css keeps
 * supplying Hearth's own values for the rest.
 */
export function themeCss(theme: ThemeCustomisation): string {
  const blocks: string[] = [];
  const light = themeCssVariables(theme.light, 'light');
  const dark = themeCssVariables(theme.dark, 'dark');
  if (Object.keys(light).length) blocks.push(`:root, .light {\n${declarations(light)}\n}`);
  if (Object.keys(dark).length) blocks.push(`.dark {\n${declarations(dark)}\n}`);
  // `.light` re-declares Hearth's radius (index.css), so the branded one goes there too.
  blocks.push(
    `:root, .light {\n${declarations({
      '--radius': RADIUS_REM[theme.appearance.radius],
      '--font-sans': fontStack(theme.typography.body),
      '--font-heading': fontStack(theme.typography.heading),
      '--font-mono': fontStack(theme.typography.mono),
    })}\n}`,
  );
  return blocks.join('\n');
}

/** The `data-*` attributes on <html> that index.css turns into appearance rules. */
export function appearanceAttributes(
  appearance: ThemeCustomisation['appearance'],
): Record<string, string> {
  return {
    'data-branded': '',
    'data-density': appearance.density,
    'data-button-style': appearance.buttonStyle,
    'data-card-style': appearance.cardStyle,
    'data-nav-style': appearance.navStyle,
    'data-sidebar-density': appearance.sidebarDensity,
  };
}

/** Split a selector list on the commas that aren't inside parentheses, brackets or strings. */
export function splitSelectors(list: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < list.length; i++) {
    const c = list[i]!;
    if (quote) {
      if (c === '\\') i++;
      else if (c === quote) quote = null;
    } else if (c === '"' || c === "'") quote = c;
    else if (c === '(' || c === '[') depth++;
    else if (c === ')' || c === ']') depth = Math.max(0, depth - 1);
    else if (c === ',' && depth === 0) {
      out.push(list.slice(start, i));
      start = i + 1;
    }
  }
  out.push(list.slice(start));
  return out.map((s) => s.trim()).filter(Boolean);
}

/**
 * Scope one selector list under `scope`. `html`, `:root` and `body` become the scope element
 * itself (a provider writing `body { … }` means the panel, and the scope is the closest thing
 * to it), keeping anything attached to them as an ancestor condition (`html.dark .x` →
 * `.dark .hearth-custom .x`). Everything else is nested under the scope.
 */
export function scopeSelector(list: string, scope: string = CUSTOM_CSS_SCOPE): string {
  return splitSelectors(list)
    .map((selector) => {
      const root = /^(?::root|html)(?![\w-])((?:[.#:[][^\s>+~]*)?)(.*)$/i.exec(selector);
      if (root) {
        const [, attached = '', rest = ''] = root;
        return attached ? `${attached} ${scope}${rest}` : `${scope}${rest}`;
      }
      const body = /^body(?![\w-])((?:[.#:[][^\s>+~]*)?)(.*)$/i.exec(selector);
      if (body) {
        const [, attached = '', rest = ''] = body;
        return `${scope}${attached}${rest}`;
      }
      return `${scope} ${selector}`;
    })
    .join(', ');
}

/** The part of a CSSOM rule that scoping needs, so the rewrite can be tested without a DOM. */
export interface RuleLike {
  selectorText?: string;
  cssRules?: ArrayLike<RuleLike>;
}

/**
 * Rewrite every style rule's selector in place, recursing into grouping rules (`@media`,
 * `@supports`, `@layer`, `@container`). Keyframe steps have no `selectorText` and are left as
 * they are. `@import` never reaches here: constructed stylesheets ignore it.
 */
export function scopeRules(rules: ArrayLike<RuleLike>, scope: string = CUSTOM_CSS_SCOPE): void {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;
    if (typeof rule.selectorText === 'string') {
      const scoped = scopeSelector(rule.selectorText, scope);
      if (scoped !== rule.selectorText) rule.selectorText = scoped;
    }
    if (rule.cssRules) scopeRules(rule.cssRules, scope);
  }
}

const cssomSupported = () =>
  typeof document !== 'undefined' &&
  typeof CSSStyleSheet !== 'undefined' &&
  typeof CSSStyleSheet.prototype.replaceSync === 'function' &&
  'adoptedStyleSheets' in document;

let themeSheet: CSSStyleSheet | null = null;
let customSheet: CSSStyleSheet | null = null;

function adopt(sheets: CSSStyleSheet[]) {
  const kept = document.adoptedStyleSheets.filter((s) => !sheets.includes(s));
  document.adoptedStyleSheets = [...kept, ...sheets];
}

/** Install (or replace) the custom CSS, scoped. Returns false when the CSS didn't parse. */
function applyCustomCss(css: string): boolean {
  customSheet ??= new CSSStyleSheet();
  try {
    customSheet.replaceSync(css);
  } catch {
    customSheet.replaceSync('');
    return false;
  }
  // Scoping happens after parsing: the browser already resolved the selectors, so a
  // selector we can't rewrite (none so far) would still fail closed inside the scope.
  scopeRules(customSheet.cssRules as ArrayLike<RuleLike>);
  return true;
}

function setFavicon(url: string | null) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.append(link);
  }
  const href = url ?? DEFAULT_FAVICON;
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  if (url) link.removeAttribute('type');
  else link.setAttribute('type', 'image/svg+xml');
}

let ourTitle: string | null = null;

/**
 * The browser title: the display name. Only replaces a title we set (or the bundled
 * "Hearth"), so a page that sets its own title keeps it.
 */
export function setDocumentTitle(name: string) {
  if (document.title === 'Hearth' || document.title === ourTitle || !document.title) {
    document.title = name;
    ourTitle = name;
  }
}

/**
 * Apply a site to the document: theme variables, appearance attributes, custom CSS, favicon,
 * the theme-colour meta and the cached default theme. Idempotent; call it whenever the site
 * or the active scheme changes.
 */
export function applySite(site: PublicSite, scheme: 'light' | 'dark' = 'dark'): void {
  const root = document.documentElement;
  if (cssomSupported()) {
    themeSheet ??= new CSSStyleSheet();
    themeSheet.replaceSync(themeCss(site.theme));
    applyCustomCss(site.customCss);
    adopt([themeSheet, customSheet!]);
  }
  for (const [name, value] of Object.entries(appearanceAttributes(site.theme.appearance))) {
    if (root.getAttribute(name) !== value) root.setAttribute(name, value);
  }
  try {
    localStorage.setItem(DEFAULT_THEME_KEY, site.theme.appearance.defaultTheme);
    localStorage.setItem(THEME_SWITCH_KEY, site.theme.appearance.allowThemeSwitch ? 'on' : 'off');
  } catch {
    // Private mode: the default is still applied by ThemeProvider after load.
  }
  setFavicon(site.logos.favicon);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = effectivePalette(site.theme[scheme], scheme).background;
}
